import { Button, Flex, Text, Tooltip } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import { SYSTEM_USER_ID } from 'app/constants/reconciliations';

import { ReconciliationFinding } from 'app/types/reconciliations';

import {
  formatDateTime,
  formatFindingType,
  formatReconciliationAmount,
  toAmount,
} from 'app/utils/reconciliations';

interface ReconciliationFindingColumnsOptions {
  showCompanyColumn?: boolean;
  showResolvedAt?: boolean;
  showLapsedAt?: boolean;
  showResolvedBy?: boolean;
  onResolve?: (finding: ReconciliationFinding) => void;
  onAccept?: (finding: ReconciliationFinding) => void;
}

/**
 * @description Names who marked a problem as fixed: the admin's full name, "System" for the system user, or their id
 * when the name can't be found.
 */
const describeResolver = ({ resolvedBy, resolvedByName }: ReconciliationFinding) => {
  if (resolvedByName) {
    return resolvedByName;
  }
  if (resolvedBy === SYSTEM_USER_ID) {
    return 'System';
  }
  return resolvedBy || '-';
};

/**
 * @description Works out how far the actual amount is from the expected one, when both are known.
 */
const differenceOf = (finding: ReconciliationFinding) => {
  const expected = toAmount(finding.expectedAmount);
  const actual = toAmount(finding.actualAmount);
  return expected === undefined || actual === undefined ? undefined : actual - expected;
};

/**
 * @description Columns of the problems a reconciliation run found for the first time or resolved, or of past problems
 * never fixed. The company column is left out in a company's own view, the resolved time is only shown for resolved
 * problems, the time a problem became past only for past ones, who fixed a problem by hand and why only for those, and
 * the actions to mark a problem as fixed or accept it only when they are given.
 */
function getReconciliationFindingColumns({
  showCompanyColumn = true,
  showResolvedAt = false,
  showLapsedAt = false,
  showResolvedBy = false,
  onResolve = undefined,
  onAccept = undefined,
}: ReconciliationFindingColumnsOptions = {}) {
  const columnHelper = createColumnHelper<ReconciliationFinding>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<ReconciliationFinding, any>[] = [
    columnHelper.accessor('companyName', {
      id: 'company',
      header: 'Company',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('individualName', {
      header: 'Person',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('payrollId', {
      header: 'Payroll ID',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('type', {
      header: 'Problem',
      enableSorting: false,
      cell: (info) => (
        <Tooltip label={info.row.original.typeDescription} placement="top" hasArrow>
          <Text>{formatFindingType(info.getValue())}</Text>
        </Tooltip>
      ),
    }),
    columnHelper.accessor('expectedAmount', {
      header: 'Expected',
      enableSorting: false,
      cell: (info) => formatReconciliationAmount(info.getValue()),
    }),
    columnHelper.accessor('actualAmount', {
      header: 'Actual',
      enableSorting: false,
      cell: (info) => formatReconciliationAmount(info.getValue()),
    }),
    columnHelper.display({
      id: 'difference',
      header: 'Difference',
      cell: (info) => formatReconciliationAmount(differenceOf(info.row.original)),
    }),
    columnHelper.accessor('firstSeenAt', {
      header: 'First seen',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.accessor('resolvedAt', {
      id: 'resolvedAt',
      header: 'Resolved',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.accessor('accepted', {
      id: 'closedAs',
      header: 'Closed as',
      enableSorting: false,
      cell: (info) => (info.getValue() ? 'Accepted' : 'Fixed'),
    }),
    columnHelper.accessor('resolvedByName', {
      id: 'resolvedBy',
      header: 'Closed by',
      enableSorting: false,
      cell: (info) => describeResolver(info.row.original),
    }),
    columnHelper.accessor('resolutionNote', {
      id: 'resolutionNote',
      header: 'Why',
      enableSorting: false,
      cell: (info) => (
        <Text maxW="360px" whiteSpace="normal">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('lapsedAt', {
      id: 'lapsedAt',
      header: 'Became past',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.accessor('detail', {
      header: 'Detail',
      enableSorting: false,
      cell: (info) => (
        <Text maxW="360px" whiteSpace="normal">
          {info.getValue() || info.row.original.typeDescription || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('id', {
      header: 'Finding ID',
      enableSorting: false,
      cell: (info) => <TextWithClipboard value={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) =>
        info.row.original.resolvedAt ? null : (
          <Flex gap="8px">
            {onResolve && (
              <Button
                size="xs"
                variant="outline"
                onClick={(event) => {
                  event.stopPropagation();
                  onResolve(info.row.original);
                }}
                data-testid={`mark-fixed-${info.row.original.id}`}
              >
                Mark as fixed
              </Button>
            )}
            {onAccept && (
              <Button
                size="xs"
                variant="outline"
                onClick={(event) => {
                  event.stopPropagation();
                  onAccept(info.row.original);
                }}
                data-testid={`accept-${info.row.original.id}`}
              >
                Accept
              </Button>
            )}
          </Flex>
        ),
    }),
  ];

  return columns.filter(
    (column) =>
      (showCompanyColumn || column.id !== 'company') &&
      (showResolvedAt || column.id !== 'resolvedAt') &&
      (showLapsedAt || column.id !== 'lapsedAt') &&
      (showResolvedBy ||
        (column.id !== 'closedAs' &&
          column.id !== 'resolvedBy' &&
          column.id !== 'resolutionNote')) &&
      (onResolve !== undefined || onAccept !== undefined || column.id !== 'actions')
  );
}

export default getReconciliationFindingColumns;
